import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { api, resolveImage } from "../lib/api";
import { Reveal } from "./Reveal";

const Gallery = ({ category = "general", fallback = [] }) => {
  const [images, setImages] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get(`/images`, { params: { category } })
      .then(({ data }) => {
        if (!mounted) return;
        if (data && data.length > 0) {
          setImages(data.map((d) => ({ url: resolveImage(d.url), alt: d.original_filename })));
        } else {
          setImages(fallback.map((u, i) => ({ url: u, alt: `Gallery photo ${i + 1}` })));
        }
      })
      .catch(() => setImages(fallback.map((u, i) => ({ url: u, alt: `Gallery photo ${i + 1}` }))));
    return () => { mounted = false; };
  }, [category]); // eslint-disable-line

  if (images.length === 0) return null;

  const spans = ["md:col-span-2 md:row-span-2", "", "", "md:row-span-2", "", "md:col-span-2"];

  const showNext = (dir) => {
    setActive((prev) => {
      if (prev === null) return prev;
      return (prev + dir + images.length) % images.length;
    });
  };

  return (
    <div data-testid="gallery">
      <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
        {images.map((img, i) => (
          <Reveal
            key={i}
            delay={(i % 4) * 0.06}
            className={`relative overflow-hidden rounded-lg group cursor-pointer ${spans[i % spans.length]}`}
          >
            <button
              onClick={() => setActive(i)}
              className="w-full h-full block"
              data-testid={`gallery-item-${i}`}
              aria-label={`Open photo ${i + 1}`}
            >
              <img
                src={img.url}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-navy/0 group-hover:bg-navy/15 transition-colors duration-500" />
            </button>
          </Reveal>
        ))}
      </div>

      <AnimatePresence>
        {active !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-navy/95 backdrop-blur-sm flex items-center justify-center p-6"
            onClick={() => setActive(null)}
            data-testid="gallery-lightbox"
          >
            <button className="absolute top-6 right-6 text-white/80 hover:text-white" onClick={() => setActive(null)} aria-label="Close">
              <X size={32} />
            </button>
            <button className="absolute left-4 md:left-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); showNext(-1); }} aria-label="Previous">
              <ChevronLeft size={40} />
            </button>
            <motion.img
              key={active}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              src={images[active].url}
              alt={images[active].alt}
              className="max-h-[85vh] max-w-[90vw] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="absolute right-4 md:right-10 text-white/70 hover:text-white" onClick={(e) => { e.stopPropagation(); showNext(1); }} aria-label="Next">
              <ChevronRight size={40} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Gallery;
